import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Calendar, Users, DollarSign, TrendingUp, MapPin, Clock, Phone, 
  Plus, Trash2, Edit, Package, Upload, Loader2, X, Music, Speaker,
  Star, UserCheck, UserX, Shield, ShieldOff, LayoutGrid, List,
  Building2, CheckCircle, XCircle, Eye, FileText, RefreshCw, Ban, Settings
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { formatDateUK, formatCurrency } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper function to safely capitalize first letter
const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper function to safely access nested properties
const safeGet = (obj, path, defaultValue = '') => {
  if (!obj) return defaultValue;
  const keys = path.split('.');
  let result = obj;
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined || result === null) return defaultValue;
  }
  return result;
};

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [equipmentBookings, setEquipmentBookings] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [systems, setSystems] = useState([]);
  const [users, setUsers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [refundStats, setRefundStats] = useState(null);
  const [processingRefund, setProcessingRefund] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedEquipmentBooking, setSelectedEquipmentBooking] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Equipment form state
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    price_unit: 'per day',
    quantity_available: 1,
    specifications: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef(null);

  // PA System form state
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [systemForm, setSystemForm] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    price_unit: 'per day',
    capacity: '',
    features: '',
    spec_power: '',
    spec_coverage: '',
    spec_setup: '',
    spec_microphones: ''
  });
  const [systemImage, setSystemImage] = useState(null);
  const [systemImagePreview, setSystemImagePreview] = useState(null);
  const systemFileInputRef = useRef(null);

  // View mode state for different sections
  const [usersViewMode, setUsersViewMode] = useState('tiles'); // 'tiles' or 'list'
  const [packagesViewMode, setPackagesViewMode] = useState('tiles');
  const [equipmentViewMode, setEquipmentViewMode] = useState('tiles');

  // Platform settings state
  const [platformSettings, setPlatformSettings] = useState({
    platform_fee_percent: 10,
    fee_waiver_end_date: null
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API}/platform/settings`, { withCredentials: true });
      setPlatformSettings({
        platform_fee_percent: response.data.platform_fee_percent ?? 10,
        fee_waiver_end_date: response.data.fee_waiver_end_date ?? null
      });
    } catch (error) {
      console.error('Failed to fetch platform settings:', error);
    }
  };

  const handleSavePlatformSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/platform/settings`, platformSettings, { withCredentials: true });
      alert('Platform settings saved successfully!');
      fetchPlatformSettings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, equipmentBookingsRes, statsRes, equipmentRes, systemsRes, usersRes, businessesRes, pendingRes, refundsRes, refundStatsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { withCredentials: true }),
        axios.get(`${API}/equipment-bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/equipment`, { withCredentials: true }),
        axios.get(`${API}/systems`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/businesses`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/businesses/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/refunds`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/refunds/stats`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setEquipmentBookings(Array.isArray(equipmentBookingsRes.data) ? equipmentBookingsRes.data : []);
      setStats(statsRes.data);
      setEquipment(Array.isArray(equipmentRes.data) ? equipmentRes.data : []);
      setSystems(Array.isArray(systemsRes.data) ? systemsRes.data : []);
      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setBusinesses(Array.isArray(businessesRes.data) ? businessesRes.data : []);
      setPendingBusinesses(Array.isArray(pendingRes.data) ? pendingRes.data : []);
      setRefunds(Array.isArray(refundsRes.data) ? refundsRes.data : []);
      setRefundStats(refundStatsRes.data || {});
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Equipment handlers
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setFormError('Please select an image file');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setFormError('');
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!selectedImage) {
      setFormError('Please select an image');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', equipmentForm.name);
      formData.append('description', equipmentForm.description);
      formData.append('category', equipmentForm.category);
      formData.append('price', equipmentForm.price);
      formData.append('price_unit', equipmentForm.price_unit);
      formData.append('quantity_available', equipmentForm.quantity_available);
      if (equipmentForm.specifications) {
        formData.append('specifications', equipmentForm.specifications);
      }
      formData.append('image', selectedImage);

      await axios.post(`${API}/admin/equipment`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      resetEquipmentForm();
      setShowAddEquipment(false);
      fetchData();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetEquipmentForm = () => {
    setEquipmentForm({
      name: '',
      description: '',
      category: '',
      price: '',
      price_unit: 'per day',
      quantity_available: 1,
      specifications: ''
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;

    try {
      await axios.delete(`${API}/admin/equipment/${equipmentId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Failed to delete equipment:', error);
    }
  };

  // PA System handlers
  const handleSystemImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setFormError('Please select an image file');
        return;
      }
      setSystemImage(file);
      setSystemImagePreview(URL.createObjectURL(file));
      setFormError('');
    }
  };

  const openAddSystemModal = () => {
    setEditingSystem(null);
    setSystemForm({
      name: '',
      category: '',
      description: '',
      price: '',
      price_unit: 'per day',
      capacity: '',
      features: '',
      spec_power: '',
      spec_coverage: '',
      spec_setup: '',
      spec_microphones: ''
    });
    setSystemImage(null);
    setSystemImagePreview(null);
    setFormError('');
    setShowSystemModal(true);
  };

  const openEditSystemModal = (system) => {
    setEditingSystem(system);
    setSystemForm({
      name: system.name,
      category: system.category,
      description: system.description,
      price: system?.price?.toString() || '',
      price_unit: system?.price_unit || 'per day',
      capacity: system?.capacity || '',
      features: system?.features?.join(', ') || '',
      spec_power: system?.specs?.power || '',
      spec_coverage: system?.specs?.coverage || '',
      spec_setup: system?.specs?.setup || '',
      spec_microphones: system?.specs?.microphones || ''
    });
    setSystemImage(null);
    const imgUrl = system?.image || '';
    setSystemImagePreview(imgUrl.startsWith('/api') ? `${BACKEND_URL}${imgUrl}` : imgUrl);
    setFormError('');
    setShowSystemModal(true);
  };

  const handleSubmitSystem = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!editingSystem && !systemImage) {
      setFormError('Please select an image');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', systemForm.name);
      formData.append('category', systemForm.category);
      formData.append('description', systemForm.description);
      formData.append('price', systemForm.price);
      formData.append('price_unit', systemForm.price_unit);
      formData.append('capacity', systemForm.capacity);
      formData.append('features', systemForm.features);
      formData.append('spec_power', systemForm.spec_power);
      formData.append('spec_coverage', systemForm.spec_coverage);
      formData.append('spec_setup', systemForm.spec_setup);
      formData.append('spec_microphones', systemForm.spec_microphones);
      
      if (systemImage) {
        formData.append('image', systemImage);
      }

      if (editingSystem) {
        await axios.put(`${API}/admin/systems/${editingSystem.system_id}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/admin/systems`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setShowSystemModal(false);
      fetchData();
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to save PA system');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSystem = async (systemId) => {
    if (!window.confirm('Are you sure you want to delete this PA system? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/systems/${systemId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete PA system');
    }
  };

  // Booking management handlers
  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, 
        { status: newStatus },
        { withCredentials: true }
      );
      fetchData();
      if (selectedBooking?.booking_id === bookingId) {
        setSelectedBooking({...selectedBooking, status: newStatus});
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update booking status');
    }
  };

  const handleUpdatePaymentStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, 
        { payment_status: newStatus },
        { withCredentials: true }
      );
      fetchData();
      if (selectedBooking?.booking_id === bookingId) {
        setSelectedBooking({...selectedBooking, payment_status: newStatus});
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update payment status');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { withCredentials: true });
      fetchData();
      setSelectedBooking(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  // Equipment booking management handlers
  const handleUpdateEquipmentBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/equipment-bookings/${bookingId}/status?status=${newStatus}`, 
        {},
        { withCredentials: true }
      );
      fetchData();
      if (selectedEquipmentBooking?.booking_id === bookingId) {
        setSelectedEquipmentBooking({...selectedEquipmentBooking, status: newStatus});
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update equipment booking status');
    }
  };

  const handleCancelEquipmentBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this equipment booking? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/equipment-bookings/${bookingId}`, { withCredentials: true });
      fetchData();
      setSelectedEquipmentBooking(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel equipment booking');
    }
  };

  // User management handlers
  const handleToggleUserActive = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { is_active: !currentStatus },
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleToggleUserAdmin = async (userId, currentStatus) => {
    const action = currentStatus ? 'remove admin rights from' : 'make admin';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    
    try {
      await axios.put(`${API}/admin/users/${userId}`, 
        { is_admin: !currentStatus },
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user admin status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      fetchData();
      setSelectedUser(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'paid': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'refunded': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const filterBookings = (status) => {
    if (status === 'all') return bookings;
    return bookings.filter(b => b.status === status);
  };

  const filterEquipmentBookings = (status) => {
    if (status === 'all') return equipmentBookings;
    return equipmentBookings.filter(b => b.status === status);
  };

  const handleProcessRefund = async (refundId, action) => {
    if (!window.confirm(`Are you sure you want to ${action === 'processed' ? 'approve' : 'reject'} this refund?`)) return;
    
    setProcessingRefund(refundId);
    try {
      await axios.put(`${API}/admin/refunds/${refundId}/process?action=${action}`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} refund`);
    } finally {
      setProcessingRefund(null);
    }
  };

  const getRefundStatusColor = (status) => {
    switch(status) {
      case 'processed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-400 text-sm">Not rated</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
          />
        ))}
        <span className="text-sm ml-1">({rating})</span>
      </div>
    );
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const BookingCard = ({ booking }) => (
    <Card className="hover:shadow-lg transition-all">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl mb-2">{booking.event_name}</CardTitle>
            <CardDescription>
              {booking.user_name} • {booking.user_email}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Badge className={`${getStatusColor(booking.status)} text-white`}>
                {booking.status}
              </Badge>
              <Badge className={`${getPaymentStatusColor(booking.payment_status)} text-white`}>
                {booking.payment_status}
              </Badge>
            </div>
            {booking.rating && renderStars(booking.rating)}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="flex items-start space-x-2">
            <Calendar className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="text-sm font-medium">{formatDateUK(booking.event_date)}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <MapPin className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="text-sm font-medium truncate">{booking.location}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <DollarSign className="h-4 w-4 text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Amount</p>
              <p className="text-sm font-bold text-blue-600">{formatCurrency(booking.total_amount)}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            PA System: <span className="font-medium text-gray-900">{booking.system_name}</span>
          </div>
          <div className="flex gap-2">
            {booking.status === 'pending' && (
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleUpdateBookingStatus(booking.booking_id, 'confirmed')}
              >
                Confirm
              </Button>
            )}
            {booking.status !== 'cancelled' && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleCancelBooking(booking.booking_id)}
              >
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedBooking(booking)}
            >
              Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const EquipmentBookingCard = ({ booking }) => (
    <Card className="hover:shadow-lg transition-all border-l-4 border-l-orange-500">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Speaker className="h-5 w-5 text-orange-500" />
              <CardTitle className="text-xl">{booking?.event_name || 'N/A'}</CardTitle>
            </div>
            <CardDescription>
              {booking?.user_name || 'Unknown'} • {booking?.user_email || 'N/A'}
            </CardDescription>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <Badge className={`${getStatusColor(booking?.status)} text-white`}>
                {booking?.status || 'pending'}
              </Badge>
              <Badge className={`${getPaymentStatusColor(booking?.payment_status)} text-white`}>
                {booking?.payment_status || 'pending'}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4 mb-4">
          <div className="flex items-start space-x-2">
            <Calendar className="h-4 w-4 text-orange-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="text-sm font-medium">{formatDateUK(booking?.event_date)}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <MapPin className="h-4 w-4 text-orange-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="text-sm font-medium truncate">{booking?.location || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <DollarSign className="h-4 w-4 text-orange-600 mt-0.5" />
            <div>
              <p className="text-xs text-muted-foreground">Amount</p>
              <p className="text-sm font-bold text-orange-600">{formatCurrency(booking?.total_amount || 0)}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Equipment: <span className="font-medium text-gray-900">{booking?.equipment_name || 'N/A'}</span>
            {booking?.quantity > 1 && <span className="ml-1">(x{booking.quantity})</span>}
          </div>
          <div className="flex gap-2">
            {booking?.status === 'pending' && (
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleUpdateEquipmentBookingStatus(booking.booking_id, 'confirmed')}
              >
                Confirm
              </Button>
            )}
            {booking?.status !== 'cancelled' && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleCancelEquipmentBooking(booking.booking_id)}
              >
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedEquipmentBooking(booking)}
            >
              Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const UserCard = ({ user }) => (
    <Card className={`hover:shadow-lg transition-all ${!user.is_active ? 'opacity-60 bg-gray-50' : ''}`}>
      <CardContent className="pt-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold text-lg">{user.name}</h3>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
          <div className="flex gap-2">
            {user.is_admin && (
              <Badge className="bg-purple-500 text-white">Admin</Badge>
            )}
            <Badge className={user.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
              {user.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
          <div>
            <p className="text-muted-foreground">Total Bookings</p>
            <p className="font-semibold">{user.total_bookings || 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Completed</p>
            <p className="font-semibold">{user.completed_bookings || 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Ratings Given</p>
            <p className="font-semibold">{user.ratings_given || 0}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Avg Rating</p>
            {user.average_rating_given ? renderStars(user.average_rating_given) : <span className="text-gray-400">-</span>}
          </div>
        </div>
        
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-xs text-muted-foreground">
            Joined: {formatDateUK(user.created_at?.split('T')[0])}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleToggleUserActive(user.user_id, user.is_active)}
              title={user.is_active ? 'Deactivate' : 'Activate'}
            >
              {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleToggleUserAdmin(user.user_id, user.is_admin)}
              title={user.is_admin ? 'Remove Admin' : 'Make Admin'}
              data-testid={`toggle-admin-card-${user.user_id}`}
            >
              {user.is_admin ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
            </Button>
            {!user.is_admin && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleDeleteUser(user.user_id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const EquipmentCard = ({ item }) => (
    <Card className="hover:shadow-lg transition-all">
      <div className="aspect-video relative overflow-hidden rounded-t-lg">
        <img
          src={item.image.startsWith('/api') ? `${BACKEND_URL}${item.image}` : item.image}
          alt={item.name}
          className="w-full h-full object-cover"
        />
        <Badge className="absolute top-2 right-2 bg-blue-600">
          {item.category}
        </Badge>
      </div>
      <CardContent className="pt-4">
        <h3 className="font-semibold text-lg mb-1">{item.name}</h3>
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{item.description}</p>
        <div className="flex justify-between items-center mb-3">
          <span className="text-xl font-bold text-blue-600">{formatCurrency(item.price)}</span>
          <span className="text-sm text-muted-foreground">{item.price_unit}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Qty: {item.quantity_available}</span>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:bg-red-50"
            onClick={() => handleDeleteEquipment(item.equipment_id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const SystemCard = ({ system }) => (
    <Card className="hover:shadow-lg transition-all">
      <div className="aspect-video relative overflow-hidden rounded-t-lg">
        <img
          src={system.image.startsWith('/api') ? `${BACKEND_URL}${system.image}` : system.image}
          alt={system.name}
          className="w-full h-full object-cover"
        />
        <Badge className="absolute top-2 right-2 bg-blue-600">
          {system.category}
        </Badge>
        {!system.available && (
          <Badge className="absolute top-2 left-2 bg-red-600">Unavailable</Badge>
        )}
      </div>
      <CardContent className="pt-4">
        <h3 className="font-semibold text-lg mb-1">{system.name}</h3>
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{system.description}</p>
        <div className="flex justify-between items-center mb-3">
          <span className="text-xl font-bold text-blue-600">{formatCurrency(system.price)}</span>
          <span className="text-sm text-muted-foreground">{system.price_unit}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-3">{system.capacity}</p>
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => openEditSystemModal(system)}
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:bg-red-50"
            onClick={() => handleDeleteSystem(system.system_id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const ViewToggle = ({ viewMode, setViewMode }) => (
    <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
      <Button
        size="sm"
        variant={viewMode === 'tiles' ? 'default' : 'ghost'}
        className={`h-8 px-3 ${viewMode === 'tiles' ? '' : 'hover:bg-gray-200'}`}
        onClick={() => setViewMode('tiles')}
        data-testid="view-toggle-tiles"
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant={viewMode === 'list' ? 'default' : 'ghost'}
        className={`h-8 px-3 ${viewMode === 'list' ? '' : 'hover:bg-gray-200'}`}
        onClick={() => setViewMode('list')}
        data-testid="view-toggle-list"
      >
        <List className="h-4 w-4" />
      </Button>
    </div>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const UsersListView = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Bookings</TableHead>
            <TableHead>Avg Rating</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map(user => (
            <TableRow key={user.user_id} className={!user.is_active ? 'opacity-60 bg-gray-50' : ''}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <Badge className={user.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </TableCell>
              <TableCell>
                {user.is_admin && <Badge className="bg-purple-500 text-white">Admin</Badge>}
              </TableCell>
              <TableCell>{user?.total_bookings || 0}</TableCell>
              <TableCell>
                {user?.average_rating_given ? (
                  <span className="flex items-center gap-1">
                    <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    {user.average_rating_given.toFixed(1)}
                  </span>
                ) : '-'}
              </TableCell>
              <TableCell>{formatDateUK(user?.created_at?.split('T')?.[0])}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => handleToggleUserActive(user.user_id, user.is_active)} title={user.is_active ? 'Deactivate' : 'Activate'}>
                    {user.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleToggleUserAdmin(user.user_id, user.is_admin)} title={user.is_admin ? 'Remove Admin' : 'Make Admin'} data-testid={`toggle-admin-${user.user_id}`}>
                    {user.is_admin ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                  </Button>
                  {!user.is_admin && (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteUser(user.user_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const SystemsListView = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Image</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Capacity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {systems.map(system => (
            <TableRow key={system.system_id}>
              <TableCell>
                <img
                  src={system.image.startsWith('/api') ? `${BACKEND_URL}${system.image}` : system.image}
                  alt={system.name}
                  className="h-12 w-16 object-cover rounded"
                />
              </TableCell>
              <TableCell className="font-medium">{system.name}</TableCell>
              <TableCell>
                <Badge className="bg-blue-600">{system.category}</Badge>
              </TableCell>
              <TableCell className="font-bold text-blue-600">{formatCurrency(system.price)}</TableCell>
              <TableCell>{system.capacity}</TableCell>
              <TableCell>
                <Badge className={system.available ? 'bg-green-500' : 'bg-red-500'}>
                  {system.available ? 'Available' : 'Unavailable'}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEditSystemModal(system)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteSystem(system.system_id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  // eslint-disable-next-line react/no-unstable-nested-components
  const EquipmentListView = () => (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Image</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Quantity</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {equipment.map(item => (
            <TableRow key={item.equipment_id}>
              <TableCell>
                <img
                  src={item.image.startsWith('/api') ? `${BACKEND_URL}${item.image}` : item.image}
                  alt={item.name}
                  className="h-12 w-16 object-cover rounded"
                />
              </TableCell>
              <TableCell className="font-medium">{item.name}</TableCell>
              <TableCell>
                <Badge className="bg-blue-600">{item.category}</Badge>
              </TableCell>
              <TableCell className="font-bold text-blue-600">{formatCurrency(item.price)}</TableCell>
              <TableCell>{item.quantity_available}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteEquipment(item.equipment_id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500" />
      </div>
    );
  }

  // Business management handlers
  const handleVerifyBusiness = async (businessId, approved) => {
    const action = approved ? 'approve' : 'reject';
    if (!window.confirm(`Are you sure you want to ${action} this business?`)) return;
    
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/verify`, 
        { approved: approved, rejection_reason: approved ? null : 'Application rejected by admin' },
        { withCredentials: true }
      );
      fetchData();
      setSelectedBusiness(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      alert(typeof errorMsg === 'string' ? errorMsg : `Failed to ${action} business`);
    }
  };

  const handleToggleBusinessActive = async (businessId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this business?`)) return;
    
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/toggle-active`, 
        {},
        { withCredentials: true }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} business`);
    }
  };

  const getVerificationStatusColor = (status) => {
    const colors = {
      verified: 'bg-green-500', pending: 'bg-yellow-500',
      rejected: 'bg-red-500', under_review: 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Platform Admin</h1>
          <p className="text-lg text-gray-400">
            Manage bookings, businesses, packages, equipment, and users
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid md:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Bookings</p>
                    <p className="text-2xl font-bold text-white">{stats.total_bookings}</p>
                  </div>
                  <Calendar className="h-5 w-5 text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Users</p>
                    <p className="text-2xl font-bold text-purple-400">{stats.total_users}</p>
                  </div>
                  <Users className="h-5 w-5 text-purple-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Businesses</p>
                    <p className="text-2xl font-bold text-blue-400">{businesses.length}</p>
                  </div>
                  <Building2 className="h-5 w-5 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Pending</p>
                    <p className="text-2xl font-bold text-orange-400">{pendingBusinesses.length}</p>
                  </div>
                  <FileText className="h-5 w-5 text-orange-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Packages</p>
                    <p className="text-2xl font-bold text-cyan-400">{stats.systems_count}</p>
                  </div>
                  <Music className="h-5 w-5 text-cyan-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Equipment</p>
                    <p className="text-2xl font-bold text-pink-400">{stats.equipment_count}</p>
                  </div>
                  <Speaker className="h-5 w-5 text-pink-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400">Revenue</p>
                    <p className="text-lg font-bold text-green-400">{formatCurrency(stats.total_revenue)}</p>
                  </div>
                  <DollarSign className="h-5 w-5 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-6 bg-gray-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">PA Bookings</TabsTrigger>
            <TabsTrigger value="equipment-bookings" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Equipment</TabsTrigger>
            <TabsTrigger value="businesses" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              Businesses {pendingBusinesses.length > 0 && <Badge className="ml-1 bg-orange-500 text-white text-xs">{pendingBusinesses.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Users</TabsTrigger>
            <TabsTrigger value="refunds" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              Refunds {refunds.filter(r => r.status === 'pending').length > 0 && <Badge className="ml-1 bg-orange-500 text-white text-xs">{refunds.filter(r => r.status === 'pending').length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Packages</TabsTrigger>
            <TabsTrigger value="inventory" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Inventory</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Settings</TabsTrigger>
          </TabsList>

          {/* Businesses Tab */}
          <TabsContent value="businesses">
            <div className="space-y-6">
              {/* Pending Applications */}
              {pendingBusinesses.length > 0 && (
                <Card className="bg-gray-800 border-gray-700 border-l-4 border-l-orange-500">
                  <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                      <FileText className="h-5 w-5 text-orange-500" />
                      Pending Business Applications ({pendingBusinesses.length})
                    </CardTitle>
                    <CardDescription className="text-gray-400">Review and approve new business registrations</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {pendingBusinesses.map((business) => (
                        <Card key={business.business_id} className="bg-gray-700 border-gray-600">
                          <CardContent className="pt-4">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                              <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center">
                                  <Building2 className="h-6 w-6 text-yellow-500" />
                                </div>
                                <div>
                                  <h3 className="text-lg font-semibold text-white">{business.business_name}</h3>
                                  <p className="text-sm text-gray-400">{business.contact_email}</p>
                                  <p className="text-sm text-gray-500">Applied: {formatDateUK(business.created_at?.split('T')[0])}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setSelectedBusiness(business)}
                                        className="border-gray-600 text-gray-300 hover:bg-gray-600">
                                  <Eye className="h-4 w-4 mr-1" /> Review
                                </Button>
                                <Button size="sm" onClick={() => handleVerifyBusiness(business.business_id, true)}
                                        className="bg-green-600 hover:bg-green-700 text-white">
                                  <CheckCircle className="h-4 w-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleVerifyBusiness(business.business_id, false)}
                                        className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white">
                                  <XCircle className="h-4 w-4 mr-1" /> Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All Businesses */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-white">All Registered Businesses ({businesses.length})</CardTitle>
                  <CardDescription className="text-gray-400">Manage all businesses on the platform</CardDescription>
                </CardHeader>
                <CardContent>
                  {businesses.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {businesses.map((business) => (
                        <Card key={business.business_id} className={`bg-gray-700 border-gray-600 ${!business.is_active ? 'opacity-60' : ''}`}>
                          <CardContent className="pt-4">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                                {business.logo_url ? (
                                  <img src={business.logo_url.startsWith('/') ? `${BACKEND_URL}${business.logo_url}` : business.logo_url} 
                                       alt="Logo" className="w-full h-full object-cover" />
                                ) : (
                                  <Building2 className="h-5 w-5 text-yellow-500" />
                                )}
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-white">{business.business_name}</h3>
                                <p className="text-xs text-gray-400">{business.contact_email}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 mb-3">
                              <Badge className={`${getVerificationStatusColor(business.verification_status)} text-white text-xs`}>
                                {business.verification_status}
                              </Badge>
                              <Badge className={business.is_active ? 'bg-green-500 text-white text-xs' : 'bg-red-500 text-white text-xs'}>
                                {business.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setSelectedBusiness(business)}
                                      className="border-gray-600 text-gray-300 hover:bg-gray-600 flex-1">
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleToggleBusinessActive(business.business_id, business.is_active)}
                                      className={business.is_active ? 'border-red-600 text-red-500 hover:bg-red-600 hover:text-white flex-1' : 'border-green-600 text-green-500 hover:bg-green-600 hover:text-white flex-1'}>
                                {business.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Building2 className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">No Businesses</h3>
                      <p className="text-gray-500">No businesses have registered yet.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PA System Bookings Tab */}
          <TabsContent value="overview">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-2xl text-white">PA System Bookings</CardTitle>
                <CardDescription className="text-gray-400">View and manage PA system bookings with ratings</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="all" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-6 bg-gray-700">
                    <TabsTrigger value="all" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">All ({bookings.length})</TabsTrigger>
                    <TabsTrigger value="confirmed" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Confirmed ({bookings.filter(b => b?.status === 'confirmed').length})</TabsTrigger>
                    <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Pending ({bookings.filter(b => b?.status === 'pending').length})</TabsTrigger>
                    <TabsTrigger value="cancelled" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Cancelled ({bookings.filter(b => b?.status === 'cancelled').length})</TabsTrigger>
                  </TabsList>
                  
                  {['all', 'confirmed', 'pending', 'cancelled'].map(status => (
                    <TabsContent key={status} value={status} className="space-y-4">
                      {filterBookings(status).length > 0 ? (
                        filterBookings(status).map(booking => (
                          <BookingCard key={booking.booking_id} booking={booking} />
                        ))
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          No {status === 'all' ? '' : status} bookings
                        </div>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipment Bookings Tab */}
          <TabsContent value="equipment-bookings">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Equipment Bookings</CardTitle>
                <CardDescription>View and manage individual equipment bookings</CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="all" className="w-full">
                  <TabsList className="grid w-full grid-cols-4 mb-6 bg-gray-700">
                    <TabsTrigger value="all" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">All ({equipmentBookings.length})</TabsTrigger>
                    <TabsTrigger value="confirmed" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Confirmed ({equipmentBookings.filter(b => b?.status === 'confirmed').length})</TabsTrigger>
                    <TabsTrigger value="pending" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Pending ({equipmentBookings.filter(b => b?.status === 'pending').length})</TabsTrigger>
                    <TabsTrigger value="cancelled" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">Cancelled ({equipmentBookings.filter(b => b?.status === 'cancelled').length})</TabsTrigger>
                  </TabsList>
                  
                  {['all', 'confirmed', 'pending', 'cancelled'].map(status => (
                    <TabsContent key={status} value={status} className="space-y-4">
                      {filterEquipmentBookings(status).length > 0 ? (
                        filterEquipmentBookings(status).map(booking => (
                          <EquipmentBookingCard key={booking.booking_id} booking={booking} />
                        ))
                      ) : (
                        <div className="text-center py-12 text-gray-500">
                          <Speaker className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          No {status === 'all' ? '' : status} equipment bookings
                        </div>
                      )}
                    </TabsContent>
                  ))}
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">Registered Users</CardTitle>
                    <CardDescription>Manage users and view their ratings</CardDescription>
                  </div>
                  <ViewToggle viewMode={usersViewMode} setViewMode={setUsersViewMode} />
                </div>
              </CardHeader>
              <CardContent>
                {users.length > 0 ? (
                  usersViewMode === 'tiles' ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {users.map(user => (
                        <UserCard key={user.user_id} user={user} />
                      ))}
                    </div>
                  ) : (
                    <UsersListView />
                  )
                ) : (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Users</h3>
                    <p className="text-gray-500">No registered users yet.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refunds Tab */}
          <TabsContent value="refunds">
            <div className="space-y-6">
              {/* Refund Stats Cards */}
              {refundStats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Pending</p>
                          <p className="text-xl font-bold text-yellow-400">{refundStats.pending_count || 0}</p>
                        </div>
                        <Clock className="h-5 w-5 text-yellow-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Processed</p>
                          <p className="text-xl font-bold text-green-400">{refundStats.processed_count || 0}</p>
                        </div>
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Rejected</p>
                          <p className="text-xl font-bold text-red-400">{refundStats.rejected_count || 0}</p>
                        </div>
                        <XCircle className="h-5 w-5 text-red-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Total Refunded</p>
                          <p className="text-xl font-bold text-blue-400">{formatCurrency(refundStats.total_refunded || 0)}</p>
                        </div>
                        <RefreshCw className="h-5 w-5 text-blue-400" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-400">Cancellation Fees</p>
                          <p className="text-xl font-bold text-purple-400">{formatCurrency(refundStats.total_cancellation_fees || 0)}</p>
                        </div>
                        <DollarSign className="h-5 w-5 text-purple-400" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Refunds Table */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-white">Refund Requests</CardTitle>
                  <CardDescription className="text-gray-400">Manage customer refund requests for cancelled bookings</CardDescription>
                </CardHeader>
                <CardContent>
                  {refunds.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-700">
                            <TableHead className="text-gray-400">Refund ID</TableHead>
                            <TableHead className="text-gray-400">Customer</TableHead>
                            <TableHead className="text-gray-400">Booking</TableHead>
                            <TableHead className="text-gray-400">Original Amount</TableHead>
                            <TableHead className="text-gray-400">Refund Amount</TableHead>
                            <TableHead className="text-gray-400">Cancel Fee</TableHead>
                            <TableHead className="text-gray-400">Requested</TableHead>
                            <TableHead className="text-gray-400">Status</TableHead>
                            <TableHead className="text-gray-400">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {refunds.map((refund) => (
                            <TableRow key={refund.refund_id} className="border-gray-700">
                              <TableCell className="font-mono text-sm text-gray-300">{refund.refund_id?.slice(0, 8)}...</TableCell>
                              <TableCell>
                                <div className="text-white">{refund.customer_name || 'N/A'}</div>
                                <div className="text-gray-400 text-sm">{refund.customer_email || ''}</div>
                              </TableCell>
                              <TableCell className="font-mono text-sm text-gray-300">{refund.booking_id?.slice(0, 8)}...</TableCell>
                              <TableCell className="text-gray-300">{formatCurrency(refund.original_amount || 0)}</TableCell>
                              <TableCell className="text-green-400 font-medium">{formatCurrency(refund.refund_amount || 0)}</TableCell>
                              <TableCell className="text-yellow-400">{formatCurrency(refund.cancellation_fee || 0)}</TableCell>
                              <TableCell className="text-gray-400 text-sm">{formatDateUK(refund.requested_at)}</TableCell>
                              <TableCell>
                                <Badge className={`${getRefundStatusColor(refund.status)} text-white`}>
                                  {refund.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {refund.status === 'pending' ? (
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handleProcessRefund(refund.refund_id, 'processed')}
                                      disabled={processingRefund === refund.refund_id}
                                      data-testid={`approve-refund-${refund.refund_id}`}
                                    >
                                      {processingRefund === refund.refund_id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="h-4 w-4" />
                                      )}
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleProcessRefund(refund.refund_id, 'rejected')}
                                      disabled={processingRefund === refund.refund_id}
                                      data-testid={`reject-refund-${refund.refund_id}`}
                                    >
                                      <Ban className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-gray-500 text-sm">
                                    {refund.status === 'processed' ? 'Approved' : 'Rejected'}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <RefreshCw className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-300 mb-2">No Refund Requests</h3>
                      <p className="text-gray-500">Refund requests from cancelled bookings will appear here.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PA Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">PA System Packages</CardTitle>
                    <CardDescription>Manage your PA system rental packages</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <ViewToggle viewMode={packagesViewMode} setViewMode={setPackagesViewMode} />
                    <Button onClick={openAddSystemModal}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Package
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {systems.length > 0 ? (
                  packagesViewMode === 'tiles' ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {systems.map(system => (
                        <SystemCard key={system.system_id} system={system} />
                      ))}
                    </div>
                  ) : (
                    <SystemsListView />
                  )
                ) : (
                  <div className="text-center py-12">
                    <Music className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No PA Packages</h3>
                    <p className="text-gray-500 mb-4">Add PA system packages for customers to book.</p>
                    <Button onClick={openAddSystemModal}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Package
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipment Inventory Tab */}
          <TabsContent value="inventory">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-2xl">Equipment Inventory</CardTitle>
                    <CardDescription>Manage individual audio equipment items</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <ViewToggle viewMode={equipmentViewMode} setViewMode={setEquipmentViewMode} />
                    <Button onClick={() => setShowAddEquipment(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Equipment
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {equipment.length > 0 ? (
                  equipmentViewMode === 'tiles' ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {equipment.map(item => (
                        <EquipmentCard key={item.equipment_id} item={item} />
                      ))}
                    </div>
                  ) : (
                    <EquipmentListView />
                  )
                ) : (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Equipment</h3>
                    <p className="text-gray-500 mb-4">Add individual equipment items.</p>
                    <Button onClick={() => setShowAddEquipment(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Equipment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Platform Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-xl text-white flex items-center gap-2">
                    <Settings className="h-6 w-6 text-yellow-500" />
                    Platform Settings
                  </CardTitle>
                  <CardDescription>Configure platform-wide settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Platform Fee Setting */}
                  <div className="p-4 bg-gray-900 rounded-lg space-y-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-green-500" />
                      Platform Fee
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Fee Percentage (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={platformSettings.platform_fee_percent}
                          onChange={(e) => setPlatformSettings({
                            ...platformSettings,
                            platform_fee_percent: parseFloat(e.target.value) || 0
                          })}
                          className="bg-gray-800 border-gray-700"
                          data-testid="platform-fee-input"
                        />
                        <p className="text-sm text-gray-500">
                          This percentage is added to all bookings as the platform service fee.
                          Set to 0 to waive platform charges.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Fee Waiver End Date (if 0%)</Label>
                        <Input
                          type="date"
                          value={platformSettings.fee_waiver_end_date ? platformSettings.fee_waiver_end_date.split('T')[0] : ''}
                          onChange={(e) => setPlatformSettings({
                            ...platformSettings,
                            fee_waiver_end_date: e.target.value ? new Date(e.target.value).toISOString() : null
                          })}
                          className="bg-gray-800 border-gray-700"
                          data-testid="fee-waiver-date-input"
                        />
                        <p className="text-sm text-gray-500">
                          If fee is 0%, a banner will show on the landing page until this date.
                          Leave empty to auto-set 30 days from now.
                        </p>
                      </div>
                    </div>
                    
                    {platformSettings.platform_fee_percent === 0 && (
                      <div className="p-3 bg-green-900/30 border border-green-700 rounded-lg">
                        <p className="text-green-400 text-sm">
                          <strong>Fee Waiver Active:</strong> Platform charges are currently waived. 
                          A promotional banner will be shown on the landing page.
                        </p>
                      </div>
                    )}
                    
                    <Button 
                      onClick={handleSavePlatformSettings}
                      disabled={savingSettings}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                      data-testid="save-platform-settings-btn"
                    >
                      {savingSettings ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Settings'
                      )}
                    </Button>
                  </div>

                  {/* Current Settings Display */}
                  <div className="p-4 bg-gray-900 rounded-lg">
                    <h3 className="text-lg font-semibold text-white mb-3">Current Configuration</h3>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <p className="text-gray-400">Platform Fee</p>
                        <p className="text-2xl font-bold text-yellow-500">{platformSettings.platform_fee_percent}%</p>
                      </div>
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <p className="text-gray-400">Fee Status</p>
                        <p className={`text-lg font-semibold ${platformSettings.platform_fee_percent === 0 ? 'text-green-500' : 'text-white'}`}>
                          {platformSettings.platform_fee_percent === 0 ? 'Waived' : 'Active'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <p className="text-gray-400">Waiver Ends</p>
                        <p className="text-lg font-semibold text-white">
                          {platformSettings.fee_waiver_end_date 
                            ? new Date(platformSettings.fee_waiver_end_date).toLocaleDateString('en-ZA')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* PA System Modal */}
      <Dialog open={showSystemModal} onOpenChange={setShowSystemModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSystem ? 'Edit PA Package' : 'Add New PA Package'}</DialogTitle>
            <DialogDescription>
              {editingSystem ? 'Update the PA system package details' : 'Create a new PA system package'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSystem} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Package Name *</Label>
                <Input
                  placeholder="e.g., Premium PA System"
                  value={systemForm.name}
                  onChange={(e) => setSystemForm({...systemForm, name: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={systemForm.category} onValueChange={(v) => setSystemForm({...systemForm, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Premium">Premium</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Conference">Conference</SelectItem>
                    <SelectItem value="Festival">Festival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea rows={2} value={systemForm.description} onChange={(e) => setSystemForm({...systemForm, description: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Price (ZAR) *</Label>
                <Input type="number" min="0" value={systemForm.price} onChange={(e) => setSystemForm({...systemForm, price: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Price Unit</Label>
                <Select value={systemForm.price_unit} onValueChange={(v) => setSystemForm({...systemForm, price_unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="per day">Per Day</SelectItem>
                    <SelectItem value="per hour">Per Hour</SelectItem>
                    <SelectItem value="per event">Per Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capacity *</Label>
                <Input placeholder="Up to 200 people" value={systemForm.capacity} onChange={(e) => setSystemForm({...systemForm, capacity: e.target.value})} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Features (comma-separated) *</Label>
              <Textarea rows={2} placeholder="4 Speakers, 2 Microphones, Mixer" value={systemForm.features} onChange={(e) => setSystemForm({...systemForm, features: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input placeholder="Power (e.g., 4000W)" value={systemForm.spec_power} onChange={(e) => setSystemForm({...systemForm, spec_power: e.target.value})} required />
              <Input placeholder="Coverage (e.g., 200 people)" value={systemForm.spec_coverage} onChange={(e) => setSystemForm({...systemForm, spec_coverage: e.target.value})} required />
              <Input placeholder="Setup Time (e.g., 30 min)" value={systemForm.spec_setup} onChange={(e) => setSystemForm({...systemForm, spec_setup: e.target.value})} required />
              <Input placeholder="Microphones (e.g., 4 wireless)" value={systemForm.spec_microphones} onChange={(e) => setSystemForm({...systemForm, spec_microphones: e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label>Image {!editingSystem && '*'}</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500" onClick={() => systemFileInputRef.current?.click()}>
                {systemImagePreview ? (
                  <img src={systemImagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                ) : (
                  <div><Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" /><p className="text-sm text-gray-500">Click to upload</p></div>
                )}
                <input ref={systemFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleSystemImageChange} />
              </div>
            </div>
            {formError && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{formError}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSystemModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}{editingSystem ? 'Update' : 'Add'} Package</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Equipment Dialog */}
      <Dialog open={showAddEquipment} onOpenChange={setShowAddEquipment}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Equipment</DialogTitle>
            <DialogDescription>Add audio equipment to inventory</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEquipment} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input placeholder="JBL Speaker" value={equipmentForm.name} onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={equipmentForm.category} onValueChange={(v) => setEquipmentForm({...equipmentForm, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Speaker">Speaker</SelectItem>
                    <SelectItem value="Microphone">Microphone</SelectItem>
                    <SelectItem value="Mixer">Mixer</SelectItem>
                    <SelectItem value="Amplifier">Amplifier</SelectItem>
                    <SelectItem value="Subwoofer">Subwoofer</SelectItem>
                    <SelectItem value="Lighting">Lighting</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea rows={2} value={equipmentForm.description} onChange={(e) => setEquipmentForm({...equipmentForm, description: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Input type="number" placeholder="Price (ZAR)" value={equipmentForm.price} onChange={(e) => setEquipmentForm({...equipmentForm, price: e.target.value})} required />
              <Select value={equipmentForm.price_unit} onValueChange={(v) => setEquipmentForm({...equipmentForm, price_unit: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per day">Per Day</SelectItem>
                  <SelectItem value="per hour">Per Hour</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" min="1" placeholder="Quantity" value={equipmentForm.quantity_available} onChange={(e) => setEquipmentForm({...equipmentForm, quantity_available: parseInt(e.target.value) || 1})} />
            </div>
            <div className="space-y-2">
              <Label>Image *</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-500" onClick={() => fileInputRef.current?.click()}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                ) : (
                  <div><Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" /><p className="text-sm text-gray-500">Click to upload</p></div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
              </div>
            </div>
            {formError && <div className="p-3 bg-red-50 text-red-700 rounded text-sm">{formError}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddEquipment(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>{submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}Add Equipment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Booking Details Dialog */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedBooking?.event_name}</DialogTitle>
            <DialogDescription>Booking ID: {selectedBooking?.booking_id}</DialogDescription>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedBooking.user_name}</span></div>
                <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{selectedBooking.user_email}</span></div>
                <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{selectedBooking.contact_phone}</span></div>
                <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDateUK(selectedBooking.event_date)}</span></div>
                <div><span className="text-muted-foreground">Time:</span> <span className="font-medium">{selectedBooking.event_time}</span></div>
                <div><span className="text-muted-foreground">Duration:</span> <span className="font-medium">{selectedBooking.duration}</span></div>
                <div><span className="text-muted-foreground">Location:</span> <span className="font-medium">{selectedBooking.location}</span></div>
                <div><span className="text-muted-foreground">Attendees:</span> <span className="font-medium">{selectedBooking.attendees}</span></div>
                <div><span className="text-muted-foreground">PA System:</span> <span className="font-medium">{selectedBooking.system_name}</span></div>
                <div><span className="text-muted-foreground">Amount:</span> <span className="font-bold text-blue-600">{formatCurrency(selectedBooking.total_amount)}</span></div>
              </div>
              
              {selectedBooking.special_requirements && (
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-muted-foreground mb-1">Special Requirements:</p>
                  <p className="text-sm">{selectedBooking.special_requirements}</p>
                </div>
              )}
              
              {/* Rating Display */}
              {selectedBooking.rating && (
                <div className="p-3 bg-yellow-50 rounded">
                  <p className="text-sm font-medium mb-2">Customer Rating:</p>
                  {renderStars(selectedBooking.rating)}
                  {selectedBooking.rating_comment && (
                    <p className="text-sm mt-2 italic">&ldquo;{selectedBooking.rating_comment}&rdquo;</p>
                  )}
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Booking Status</Label>
                  <Select value={selectedBooking.status} onValueChange={(v) => handleUpdateBookingStatus(selectedBooking.booking_id, v)} disabled={selectedBooking.status === 'cancelled'}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Status</Label>
                  <Select value={selectedBooking.payment_status} onValueChange={(v) => handleUpdatePaymentStatus(selectedBooking.booking_id, v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {selectedBooking.status !== 'cancelled' && (
                <Button variant="destructive" className="w-full" onClick={() => handleCancelBooking(selectedBooking.booking_id)}>
                  <Trash2 className="h-4 w-4 mr-2" />Cancel Booking
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Equipment Booking Details Dialog */}
      <Dialog open={!!selectedEquipmentBooking} onOpenChange={() => setSelectedEquipmentBooking(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Speaker className="h-5 w-5 text-orange-500" />
              <DialogTitle>{selectedEquipmentBooking?.event_name || 'Equipment Booking'}</DialogTitle>
            </div>
            <DialogDescription>Booking ID: {selectedEquipmentBooking?.booking_id || 'N/A'}</DialogDescription>
          </DialogHeader>
          {selectedEquipmentBooking && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedEquipmentBooking?.user_name || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{selectedEquipmentBooking?.user_email || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Phone:</span> <span className="font-medium">{selectedEquipmentBooking?.contact_phone || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDateUK(selectedEquipmentBooking?.event_date)}</span></div>
                <div><span className="text-muted-foreground">Time:</span> <span className="font-medium">{selectedEquipmentBooking?.event_time || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Duration:</span> <span className="font-medium">{selectedEquipmentBooking?.duration || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Location:</span> <span className="font-medium">{selectedEquipmentBooking?.location || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Attendees:</span> <span className="font-medium">{selectedEquipmentBooking?.attendees || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Equipment:</span> <span className="font-medium">{selectedEquipmentBooking?.equipment_name || 'N/A'}</span></div>
                <div><span className="text-muted-foreground">Quantity:</span> <span className="font-medium">{selectedEquipmentBooking?.quantity || 1}</span></div>
                <div><span className="text-muted-foreground">Amount:</span> <span className="font-bold text-orange-600">{formatCurrency(selectedEquipmentBooking?.total_amount || 0)}</span></div>
              </div>
              
              {selectedEquipmentBooking?.special_requirements && (
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-sm text-muted-foreground mb-1">Special Requirements:</p>
                  <p className="text-sm">{selectedEquipmentBooking.special_requirements}</p>
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Booking Status</Label>
                  <Select 
                    value={selectedEquipmentBooking?.status || 'pending'} 
                    onValueChange={(v) => handleUpdateEquipmentBookingStatus(selectedEquipmentBooking.booking_id, v)} 
                    disabled={selectedEquipmentBooking?.status === 'cancelled'}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Payment Status</Label>
                  <Badge className={`${getPaymentStatusColor(selectedEquipmentBooking?.payment_status)} text-white`}>
                    {selectedEquipmentBooking?.payment_status || 'pending'}
                  </Badge>
                </div>
              </div>
              
              {selectedEquipmentBooking?.status !== 'cancelled' && (
                <Button variant="destructive" className="w-full" onClick={() => handleCancelEquipmentBooking(selectedEquipmentBooking.booking_id)}>
                  <Trash2 className="h-4 w-4 mr-2" />Cancel Booking
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Business Details Dialog */}
      <Dialog open={!!selectedBusiness} onOpenChange={() => setSelectedBusiness(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-yellow-500" />
              {selectedBusiness?.business_name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Business ID: {selectedBusiness?.business_id}
            </DialogDescription>
          </DialogHeader>
          {selectedBusiness && (
            <div className="space-y-6">
              <div className="flex gap-2">
                <Badge className={`${getVerificationStatusColor(selectedBusiness.verification_status)} text-white`}>
                  {selectedBusiness.verification_status}
                </Badge>
                <Badge className={selectedBusiness.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                  {selectedBusiness.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-400">Contact Email:</span> <span className="font-medium text-white">{selectedBusiness.contact_email}</span></div>
                <div><span className="text-gray-400">Contact Phone:</span> <span className="font-medium text-white">{selectedBusiness.contact_phone || 'N/A'}</span></div>
                <div><span className="text-gray-400">Owner ID:</span> <span className="font-medium text-white">{selectedBusiness.user_id}</span></div>
                <div><span className="text-gray-400">Created:</span> <span className="font-medium text-white">{formatDateUK(selectedBusiness.created_at?.split('T')[0])}</span></div>
              </div>

              {selectedBusiness.description && (
                <div className="p-3 bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Description:</p>
                  <p className="text-sm text-white">{selectedBusiness.description}</p>
                </div>
              )}

              {selectedBusiness.address && (
                <div className="p-3 bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Address:</p>
                  <p className="text-sm text-white">
                    {selectedBusiness.address.street}, {selectedBusiness.address.city}, {selectedBusiness.address.province} {selectedBusiness.address.postal_code}
                  </p>
                </div>
              )}

              {selectedBusiness.verification_docs && (
                <div className="p-3 bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Verification Documents:</p>
                  <div className="space-y-2">
                    {selectedBusiness.verification_docs.id_document && (
                      <a href={`${BACKEND_URL}${selectedBusiness.verification_docs.id_document}`} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 text-sm">
                        <FileText className="h-4 w-4" /> ID Document
                      </a>
                    )}
                    {selectedBusiness.verification_docs.address_proof && (
                      <a href={`${BACKEND_URL}${selectedBusiness.verification_docs.address_proof}`} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 text-sm">
                        <FileText className="h-4 w-4" /> Address Proof
                      </a>
                    )}
                    {selectedBusiness.verification_docs.bank_confirmation && (
                      <a href={`${BACKEND_URL}${selectedBusiness.verification_docs.bank_confirmation}`} target="_blank" rel="noopener noreferrer"
                         className="flex items-center gap-2 text-yellow-500 hover:text-yellow-400 text-sm">
                        <FileText className="h-4 w-4" /> Bank Confirmation
                      </a>
                    )}
                  </div>
                </div>
              )}

              {selectedBusiness.verification_status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-gray-700">
                  <Button onClick={() => handleVerifyBusiness(selectedBusiness.business_id, true)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                    <CheckCircle className="h-4 w-4 mr-2" /> Approve Business
                  </Button>
                  <Button variant="outline" onClick={() => handleVerifyBusiness(selectedBusiness.business_id, false)}
                          className="flex-1 border-red-600 text-red-500 hover:bg-red-600 hover:text-white">
                    <XCircle className="h-4 w-4 mr-2" /> Reject Business
                  </Button>
                </div>
              )}

              {selectedBusiness.verification_status === 'verified' && (
                <div className="flex gap-3 pt-4 border-t border-gray-700">
                  <Button variant="outline" onClick={() => handleToggleBusinessActive(selectedBusiness.business_id, selectedBusiness.is_active)}
                          className={selectedBusiness.is_active 
                            ? 'flex-1 border-red-600 text-red-500 hover:bg-red-600 hover:text-white' 
                            : 'flex-1 border-green-600 text-green-500 hover:bg-green-600 hover:text-white'}>
                    {selectedBusiness.is_active ? (
                      <><XCircle className="h-4 w-4 mr-2" /> Deactivate Business</>
                    ) : (
                      <><CheckCircle className="h-4 w-4 mr-2" /> Activate Business</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
